import { Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, Divider, Stack } from '@mui/joy';
import React, { ChangeEvent, FC } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthHeader, ReverificationProvider, useReverificationStore } from '@MP/features/auth';
import { useFetchCurrentUser, useUpdateAgreement } from '@MP/shared/api/hooks/auth';
import { RoundButton } from '@MP/shared/components/Button';
import useLogout from '@MP/shared/hooks/useLogout';

const ReverificationPage = () => {
  const { data: currentUser, isError } = useFetchCurrentUser();

  if (isError) {
    return <Navigate to={'/login'} replace />;
  }

  if (currentUser?.isSubscribeMarketingEvent !== null) {
    return <Navigate to={'/'} replace />;
  }

  return (
    <ReverificationProvider>
      <ReverificationContents />
    </ReverificationProvider>
  );
};

const ReverificationContents = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout } = useLogout();

  const eulaAgree = useReverificationStore((s) => s.eulaAgree);
  const privacyAgree = useReverificationStore((s) => s.privacyAgree);
  const isSubscribeMarketingEvent = useReverificationStore((s) => s.isSubscribeMarketingEvent);
  const setAllAgree = useReverificationStore((s) => s.setAllAgree);
  const setEulaAgree = useReverificationStore((s) => s.setEulaAgree);
  const setPrivacyAgree = useReverificationStore((s) => s.setPrivacyAgree);
  const setSubscribeMarketingEvent = useReverificationStore((s) => s.setSubscribeMarketingEvent);

  const isRequiredAllAgree = eulaAgree && privacyAgree;
  const isAllAgree = isRequiredAllAgree && isSubscribeMarketingEvent;

  const { data: currentUser } = useFetchCurrentUser();
  const { mutate: updateAgreement, isPending } = useUpdateAgreement();

  const handleSubmit = () => {
    if (!currentUser) return;
    updateAgreement(
      {
        userId: currentUser.id as number,
        update: { isSubscribeMarketingEvent },
      },
      {
        onSuccess: () => {
          const redirectUrl = searchParams.get('redirectUrl');
          if (redirectUrl) {
            document.location.replace(redirectUrl);
          } else {
            navigate('/');
          }
        },
      }
    );
  };

  return (
    <Box bgcolor={'#F5F5F5'} height={'100vh'} overflow={'auto'} pb={'16px'}>
      <AuthHeader title="회원 가입" steps={['약관 동의']} active={0} />
      <Stack p={'24px'} spacing={'16px'} alignItems={'center'}>
        <AgreementsItem checked={isAllAgree} title={'전체 약관 동의'} onChange={(checked) => setAllAgree(checked)} />
        <AgreementsItem
          checked={eulaAgree}
          title={'MINDD 서비스 이용약관 동의 (필수)'}
          url="/agreements/web/eula.html"
          onChange={(checked) => setEulaAgree(checked)}
        />
        <AgreementsItem
          checked={privacyAgree}
          title={'MINDD 개인정보 수집 및 이용 동의 (필수)'}
          url="/agreements/web/privacy.html"
          onChange={(checked) => setPrivacyAgree(checked)}
        />
        <AgreementsItem
          checked={isSubscribeMarketingEvent}
          title={'마케팅 정보 수신 동의 (선택)'}
          onChange={(checked) => setSubscribeMarketingEvent(checked)}
        />
      </Stack>
      <Stack direction={'row'} spacing={'12px'} justifyContent={'center'}>
        <RoundButton variant="outlined" onClick={() => logout()}>
          취소
        </RoundButton>
        <RoundButton disabled={!isRequiredAllAgree} loading={isPending} onClick={handleSubmit}>
          가입완료
        </RoundButton>
      </Stack>
    </Box>
  );
};

const AgreementsItem: FC<{
  checked: boolean;
  title: string;
  url?: string;
  onChange: (checked: boolean) => void;
}> = ({ checked, title, url, onChange }) => {
  const [expanded, setExpanded] = React.useState(false);

  const handleCheckedChange = (e: ChangeEvent) => {
    e.stopPropagation();

    onChange(!checked);
  };

  if (!url) {
    return (
      <Accordion
        sx={{
          width: 800,
          borderRadius: '8px',
          bgcolor: '#FFF',
          p: '8px',
          '--joy-palette-neutral-plainHoverBg': '#FFF',
          '--joy-palette-neutral-plainActiveBg': '#FFF',
        }}
        onChange={(event, expanded) => {
          if ((event.target as HTMLElement)?.tagName.toLowerCase() !== 'input') {
            setExpanded(expanded);
          }
        }}>
        <AccordionSummary indicator={null} slotProps={{ button: { sx: { p: '16px' } } }}>
          <Checkbox checked={checked} label={title} onChange={handleCheckedChange} />
        </AccordionSummary>
      </Accordion>
    );
  }

  return (
    <Accordion
      sx={{
        width: 800,
        borderRadius: '8px',
        bgcolor: '#FFF',
        p: '8px',
        '--joy-palette-neutral-plainHoverBg': '#FFF',
        '--joy-palette-neutral-plainActiveBg': '#FFF',
      }}
      expanded={expanded}
      onChange={(event, expanded) => {
        if ((event.target as HTMLElement)?.tagName.toLowerCase() !== 'input') {
          setExpanded(expanded);
        }
      }}>
      <AccordionSummary slotProps={{ button: { sx: { p: '16px' } } }}>
        <Checkbox checked={checked} label={title} onChange={handleCheckedChange} />
      </AccordionSummary>
      <Box sx={{ px: '16px' }}>
        <Divider />
      </Box>
      <AccordionDetails
        sx={{ p: '16px', gridTemplateRows: '500px', ':not(.Mui-expanded)': { gridTemplateRows: '200px' } }}>
        <object type="text/html" data={url} style={{ width: '100%', height: '100%' }} aria-label={url} />
      </AccordionDetails>
    </Accordion>
  );
};

export default ReverificationPage;
